import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, Users, Building2, Trophy, Target,
  RefreshCw, BarChart3, Activity, Calendar, Star, Zap, Award, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Area, AreaChart, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VertriebPerformancePage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('salesperson_token');
  const salespersonId = localStorage.getItem('salesperson_id');
  
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!token || !salespersonId) {
      navigate('/vertrieb/login');
      return;
    }
    loadData();
  }, [token, salespersonId, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [perfRes, leaderRes] = await Promise.all([
        fetch(`${API_URL}/api/salespeople/performance/my`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/salespeople/performance/leaderboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (perfRes.ok) {
        const perfData = await perfRes.json();
        setData(perfData);
      }
      
      if (leaderRes.ok) {
        const leaderData = await leaderRes.json();
        setLeaderboard(leaderData);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subvalue, color, highlight }) => (
    <div className={`bg-white rounded-xl border-2 p-5 transition-all ${highlight ? 'border-brand-green shadow-lg' : 'border-gray-200'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {subvalue && <div className="text-xs text-gray-500 mt-1">{subvalue}</div>}
    </div>
  );

  const getRankBadge = (rank) => {
    if (rank === 1) return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '🥇' };
    if (rank === 2) return { bg: 'bg-gray-100', text: 'text-gray-700', icon: '🥈' };
    if (rank === 3) return { bg: 'bg-orange-100', text: 'text-orange-700', icon: '🥉' };
    return { bg: 'bg-gray-50', text: 'text-gray-600', icon: rank };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  const myRank = leaderboard?.leaderboard?.find(l => l.id === salespersonId)?.rank || '-';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/vertrieb/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-brand-green" />
              Meine Performance
            </h1>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadData}
            className="text-gray-600"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-brand-green to-emerald-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">
                Hallo, {data?.salesperson?.name || 'Vertriebler'}!
              </h2>
              <p className="text-white/80">
                {data?.comparison?.above_average 
                  ? `Du liegst ${data.comparison.diff_from_avg} Partner über dem Team-Durchschnitt!`
                  : `Noch ${Math.abs(data?.comparison?.diff_from_avg || 0)} Partner bis zum Team-Durchschnitt.`
                }
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold">{myRank}</div>
                <div className="text-sm text-white/70">Dein Rang</div>
              </div>
              <div className="w-px h-12 bg-white/30"></div>
              <div className="text-center">
                <div className="text-3xl font-bold">{data?.highlights?.current_streak_weeks || 0}</div>
                <div className="text-sm text-white/70">Wochen-Streak</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'overview' 
                ? 'bg-brand-green text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="tab-overview"
          >
            Übersicht
          </button>
          <button
            onClick={() => setActiveTab('trends')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'trends' 
                ? 'bg-brand-green text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="tab-trends"
          >
            Trends
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'leaderboard' 
                ? 'bg-brand-green text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="tab-leaderboard"
          >
            Rangliste
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                icon={Users} 
                label="Meine Partner" 
                value={data?.summary?.total_partners || 0}
                subvalue={`+${data?.summary?.partners_30d || 0} in 30 Tagen`}
                color="bg-brand-green/10 text-brand-green"
                highlight={true}
              />
              <StatCard 
                icon={Building2} 
                label="Besuche" 
                value={data?.summary?.total_businesses_visited || 0}
                subvalue={`von ${data?.summary?.total_businesses_in_tours || 0} geplant`}
                color="bg-blue-100 text-blue-600"
              />
              <StatCard 
                icon={Target} 
                label="Konvertierung" 
                value={`${data?.summary?.conversion_rate || 0}%`}
                subvalue="Besuch → Partner"
                color="bg-purple-100 text-purple-600"
              />
              <StatCard 
                icon={Calendar} 
                label="Touren" 
                value={data?.summary?.total_tours || 0}
                subvalue={`${data?.summary?.completed_tours || 0} abgeschlossen`}
                color="bg-orange-100 text-orange-600"
              />
            </div>

            {/* Comparison Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-brand-green" />
                Team-Vergleich
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-3xl font-bold text-gray-900">{data?.summary?.total_partners || 0}</div>
                  <div className="text-sm text-gray-600 mt-1">Deine Partner</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-3xl font-bold text-gray-500">{data?.comparison?.team_avg_partners || 0}</div>
                  <div className="text-sm text-gray-600 mt-1">Team-Durchschnitt</div>
                </div>
                <div className={`text-center p-4 rounded-xl ${data?.comparison?.above_average ? 'bg-green-50' : 'bg-orange-50'}`}>
                  <div className={`text-3xl font-bold ${data?.comparison?.above_average ? 'text-green-600' : 'text-orange-600'}`}>
                    {data?.comparison?.above_average ? '+' : ''}{data?.comparison?.diff_from_avg || 0}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {data?.comparison?.above_average ? 'Über Durchschnitt' : 'Unter Durchschnitt'}
                  </div>
                </div>
              </div>
            </div>

            {/* Highlights */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Star className="w-6 h-6 text-yellow-500" />
                  <span className="font-semibold text-gray-900">Bester Monat</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {data?.highlights?.best_month?.month || '-'}
                </div>
                <div className="text-sm text-gray-600">
                  {data?.highlights?.best_month?.partners || 0} Partner registriert
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-6 h-6 text-purple-500" />
                  <span className="font-semibold text-gray-900">Aktuelle Serie</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {data?.highlights?.current_streak_weeks || 0} Wochen
                </div>
                <div className="text-sm text-gray-600">
                  mit Partner-Registrierung
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Activity className="w-6 h-6 text-green-500" />
                  <span className="font-semibold text-gray-900">Besuchs-Effizienz</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {data?.highlights?.visit_efficiency || 0}%
                </div>
                <div className="text-sm text-gray-600">
                  der geplanten Besuche absolviert
                </div>
              </div>
            </div>
          </>
        )}

        {/* Trends Tab */}
        {activeTab === 'trends' && (
          <>
            {/* Monthly Partners Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-brand-green" />
                Partner pro Monat (letzte 6 Monate)
              </h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data?.monthly_data || []}>
                    <defs>
                      <linearGradient id="colorPartners" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#38B840" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#38B840" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month_short" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.month || label}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="partners" 
                      stroke="#38B840" 
                      strokeWidth={2}
                      fill="url(#colorPartners)" 
                      name="Partner"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="tours" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Touren"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Weekly Trend */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Wöchentliche Aktivität (letzte 8 Wochen)
              </h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.weekly_data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                    />
                    <Legend />
                    <Bar dataKey="partners" name="Partner" fill="#38B840" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="visits" name="Besuche" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Team-Rangliste
            </h3>
            <div className="space-y-2">
              {(leaderboard?.leaderboard || []).map((entry) => {
                const badge = getRankBadge(entry.rank);
                const isMe = entry.id === salespersonId;
                
                return (
                  <div 
                    key={entry.id}
                    className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                      isMe 
                        ? 'bg-brand-green/10 border-2 border-brand-green' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    data-testid={`leaderboard-entry-${entry.rank}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${badge.bg} ${badge.text}`}>
                        {typeof badge.icon === 'string' ? badge.icon : entry.rank}
                      </div>
                      <div>
                        <div className={`font-semibold ${isMe ? 'text-brand-green' : 'text-gray-900'}`}>
                          {entry.name} {isMe && '(Du)'}
                        </div>
                        {entry.region && (
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {entry.region}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="font-bold text-gray-900">{entry.partners}</div>
                        <div className="text-xs text-gray-500">Partner</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-gray-900">{entry.tours}</div>
                        <div className="text-xs text-gray-500">Touren</div>
                      </div>
                      <div className={`w-16 text-center px-2 py-1 rounded-full text-sm font-semibold ${
                        entry.rank <= 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {entry.score} Pkt
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {(!leaderboard?.leaderboard || leaderboard.leaderboard.length === 0) && (
                <div className="text-center text-gray-500 py-8">
                  <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Keine Ranglisten-Daten verfügbar</p>
                </div>
              )}
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default VertriebPerformancePage;
